/**
 * PDF Generation Service
 * Creates PDF documents for proposals, contracts, and reports
 */

import jsPDF from 'jspdf';
import 'jspdf-autotable';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { storage } from './firebase';
import { Proposal } from '../types/proposal';

// Extend jsPDF type to include autoTable
declare module 'jspdf' {
  interface jsPDF {
    autoTable: (options: any) => jsPDF;
    lastAutoTable?: { finalY: number };
  }
}

/**
 * Generate PDF from proposal data
 */
export const generateProposalPDF = async (proposal: Proposal): Promise<Blob> => {
  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  let yPos = 20;

  // Header
  doc.setFontSize(24);
  doc.setTextColor(41, 128, 185); // Brand blue
  doc.text('DossiLoc', 20, yPos);
  
  doc.setFontSize(10);
  doc.setTextColor(100, 100, 100);
  doc.text('Complete rent collection automation', 20, yPos + 7);
  
  // Proposal Title
  yPos = 50;
  doc.setFontSize(20);
  doc.setTextColor(0, 0, 0);
  doc.text(proposal.title, 20, yPos);
  
  // Metadata
  yPos += 15;
  doc.setFontSize(10);
  doc.setTextColor(100, 100, 100);
  doc.text(`Prepared for: ${proposal.leadName}`, 20, yPos);
  yPos += 6;
  doc.text(`Company: ${proposal.leadCompany || 'N/A'}`, 20, yPos);
  yPos += 6;
  doc.text(`Date: ${new Date().toLocaleDateString('en-CA')}`, 20, yPos);
  yPos += 6;
  const validUntilDate = proposal.validUntil instanceof Date 
    ? proposal.validUntil 
    : (proposal.validUntil && typeof proposal.validUntil === 'object' && 'toDate' in proposal.validUntil 
      ? (proposal.validUntil as any).toDate() 
      : new Date());
  doc.text(`Valid Until: ${validUntilDate.toLocaleDateString('en-CA')}`, 20, yPos);
  
  // Summary Section
  yPos += 15;
  doc.setFontSize(14);
  doc.setTextColor(0, 0, 0);
  doc.text('Executive Summary', 20, yPos);
  
  yPos += 10;
  doc.setFontSize(10);
  doc.setTextColor(60, 60, 60);
  const summarySection = proposal.sections.find(s => s.id === 'executive-summary');
  const summaryText = summarySection?.content || 'Custom property management solution designed for your needs.';
  const summaryLines = doc.splitTextToSize(summaryText, pageWidth - 40);
  doc.text(summaryLines, 20, yPos);
  yPos += summaryLines.length * 6 + 10;

  // Pricing Section
  if (yPos > pageHeight - 60) {
    doc.addPage();
    yPos = 20;
  }

  doc.setFontSize(14);
  doc.setTextColor(0, 0, 0);
  doc.text('Pricing', 20, yPos);
  yPos += 10;

  const pricingData = [
    ['Plan Type', proposal.pricing.pricingTier.toUpperCase()],
    ['Monthly Fee', `$${proposal.pricing.finalPrice.toLocaleString('en-CA')}`],
    ['Annual Fee', `$${(proposal.pricing.finalPrice * 12).toLocaleString('en-CA')}`],
    ['Discount', proposal.pricing.discountPercent ? `${proposal.pricing.discountPercent}%` : 'None'],
  ];

  doc.autoTable({
    startY: yPos,
    head: [['Item', 'Value']],
    body: pricingData,
    theme: 'striped',
    headStyles: { fillColor: [41, 128, 185] },
    margin: { left: 20, right: 20 },
  });

  yPos = doc.lastAutoTable!.finalY + 15;

  // Features Section
  if (proposal.features && proposal.features.length > 0) {
    if (yPos > pageHeight - 60) {
      doc.addPage();
      yPos = 20;
    }

    doc.setFontSize(14);
    doc.setTextColor(0, 0, 0);
    doc.text('Included Features', 20, yPos);
    yPos += 10;

    const featuresData = proposal.features.map(feature => [
      feature.name,
      feature.included ? '✓' : '—',
      feature.description || '',
    ]);

    doc.autoTable({
      startY: yPos,
      head: [['Feature', 'Included', 'Description']],
      body: featuresData,
      theme: 'striped',
      headStyles: { fillColor: [41, 128, 185] },
      margin: { left: 20, right: 20 },
      columnStyles: {
        0: { cellWidth: 50 },
        1: { cellWidth: 20, halign: 'center' },
        2: { cellWidth: 'auto' },
      },
    });

    yPos = doc.lastAutoTable!.finalY + 15;
  }

  // Custom Sections
  if (proposal.sections && proposal.sections.length > 0) {
    proposal.sections.forEach((section) => {
      if (yPos > pageHeight - 60) {
        doc.addPage();
        yPos = 20;
      }

      doc.setFontSize(14);
      doc.setTextColor(0, 0, 0);
      doc.text(section.title, 20, yPos);
      yPos += 10;

      doc.setFontSize(10);
      doc.setTextColor(60, 60, 60);
      const contentLines = doc.splitTextToSize(section.content, pageWidth - 40);
      doc.text(contentLines, 20, yPos);
      yPos += contentLines.length * 6 + 15;
    });
  }

  // Footer on last page
  const totalPages = doc.getNumberOfPages();
  for (let i = 1; i <= totalPages; i++) {
    doc.setPage(i);
    doc.setFontSize(8);
    doc.setTextColor(150, 150, 150);
    doc.text(
      `Page ${i} of ${totalPages}`,
      pageWidth / 2,
      pageHeight - 10,
      { align: 'center' }
    );
    doc.text(
      'DossiLoc • sales@dossiloc.ca • dossiloc.ca',
      pageWidth / 2,
      pageHeight - 5,
      { align: 'center' }
    );
  }

  return doc.output('blob');
};

/**
 * Upload PDF to Firebase Storage and return download URL
 */
export const uploadProposalPDF = async (
  proposalId: string,
  pdfBlob: Blob
): Promise<string> => {
  try {
    const fileName = `proposals/${proposalId}/proposal_${Date.now()}.pdf`;
    const storageRef = ref(storage, fileName);

    await uploadBytes(storageRef, pdfBlob, {
      contentType: 'application/pdf',
    });

    const downloadURL = await getDownloadURL(storageRef);
    return downloadURL;
  } catch (error) {
    console.error('Error uploading PDF to storage:', error);
    throw new Error('Failed to upload PDF');
  }
};

/**
 * Generate and upload proposal PDF
 * Returns the download URL
 */
export const generateAndUploadProposalPDF = async (
  proposal: Proposal
): Promise<string> => {
  try {
    const pdfBlob = await generateProposalPDF(proposal);
    const downloadURL = await uploadProposalPDF(proposal.id, pdfBlob);
    return downloadURL;
  } catch (error) {
    console.error('Error generating and uploading PDF:', error);
    throw new Error('Failed to generate and upload PDF');
  }
};

/**
 * Download PDF directly to user's device
 */
export const downloadProposalPDF = async (proposal: Proposal): Promise<void> => {
  try {
    const pdfBlob = await generateProposalPDF(proposal);
    const url = URL.createObjectURL(pdfBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${proposal.title.replace(/[^a-z0-9]/gi, '_')}_${proposal.id}.pdf`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  } catch (error) {
    console.error('Error downloading PDF:', error);
    throw new Error('Failed to download PDF');
  }
};

/**
 * Generate contract PDF
 */
export const generateContractPDF = async (contract: any): Promise<Blob> => {
  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  let yPos = 20;

  // Header
  doc.setFontSize(24);
  doc.setTextColor(41, 128, 185);
  doc.text('Service Contract', 20, yPos);
  
  yPos += 15;
  doc.setFontSize(10);
  doc.setTextColor(100, 100, 100);
  doc.text(`Contract ID: ${contract.id}`, 20, yPos);
  yPos += 6;
  doc.text(`Date: ${new Date().toLocaleDateString('en-CA')}`, 20, yPos);

  // Contract Details
  yPos += 15;
  doc.setFontSize(14);
  doc.setTextColor(0, 0, 0);
  doc.text('Contract Details', 20, yPos);
  yPos += 10;

  const contractData = [
    ['Landlord', contract.landlordName],
    ['Property', contract.propertyAddress],
    ['Start Date', contract.startDate.toDate().toLocaleDateString('en-CA')],
    ['End Date', contract.endDate.toDate().toLocaleDateString('en-CA')],
    ['Monthly Rent', `$${contract.monthlyRent.toLocaleString('en-CA')}`],
    ['Status', contract.status.toUpperCase()],
  ];

  doc.autoTable({
    startY: yPos,
    head: [['Field', 'Value']],
    body: contractData,
    theme: 'striped',
    headStyles: { fillColor: [41, 128, 185] },
    margin: { left: 20, right: 20 },
  });

  // Terms
  yPos = doc.lastAutoTable!.finalY + 15;
  if (contract.terms) {
    doc.setFontSize(14);
    doc.text('Terms & Conditions', 20, yPos);
    yPos += 10;

    doc.setFontSize(10);
    doc.setTextColor(60, 60, 60);
    const termsLines = doc.splitTextToSize(contract.terms, pageWidth - 40);
    doc.text(termsLines, 20, yPos);
  }

  // Footer
  const totalPages = doc.getNumberOfPages();
  for (let i = 1; i <= totalPages; i++) {
    doc.setPage(i);
    doc.setFontSize(8);
    doc.setTextColor(150, 150, 150);
    doc.text(
      `Page ${i} of ${totalPages}`,
      pageWidth / 2,
      pageHeight - 10,
      { align: 'center' }
    );
  }

  return doc.output('blob');
};

/**
 * Download contract PDF
 */
export const downloadContractPDF = async (contract: any): Promise<void> => {
  try {
    const pdfBlob = await generateContractPDF(contract);
    const url = URL.createObjectURL(pdfBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `contract_${contract.id}.pdf`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  } catch (error) {
    console.error('Error downloading contract PDF:', error);
    throw new Error('Failed to download contract PDF');
  }
};
